/**
 * functions common time
 *
 * @category   Javascript
 * @author     Jakob Ohlsen <jakob.ohlsen@gmail.com>
 * @copyright  2025 Jakob Ohlsen
 */
export function secToHhmm(seconds, floorMinutes = false, padHours = true) {
    const h = Math.floor(seconds / 3600);
    const m = floorMinutes ? Math.floor((seconds - h * 3600) / 60) : Math.round((seconds - h * 3600) / 60);
    const pad = (num) => num.toString().padStart(2, '0');
    if (padHours)
        return `${pad(h)}:${pad(m)}`;
    else
        return `${h}:${pad(m)}`;
}
export function secToHhmmss(seconds, floorSeconds = false, padHours = true) {
    seconds = floorSeconds ? Math.floor(seconds) : Math.round(seconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const pad = (num) => num.toString().padStart(2, '0');
    if (padHours)
        return `${pad(h)}:${pad(m)}:${pad(s)}`;
    else
        return `${h}:${pad(m)}:${pad(s)}`;
}
export function secToMmss(seconds, floorSeconds = false) {
    seconds = floorSeconds ? Math.floor(seconds) : Math.round(seconds);
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    const pad = (num) => num.toString().padStart(2, '0');
    return `${m}:${pad(s)}`;
}
export function formatDate(dateInput, format) {
    let dateObj = new Date(dateInput);
    const two = (s) => (s < 10 ? '0' + String(s) : String(s));
    const lastTwo = (s) => (s.length >= 2 ? String(s).slice(-2) : String(s));
    const ret = format
        .replace(/([^*])(?=(?:[^"]*"[^"]*")*[^"]*$)/g, (match) => {
        switch (match) {
            case 'd':
                return two(dateObj.getDate());
            case 'j':
                return String(dateObj.getDate());
            case 'm':
                return two(dateObj.getMonth() + 1);
            case 'n':
                return two(dateObj.getMonth() + 1);
            case 'Y':
                return String(dateObj.getFullYear());
            case 'y':
                return lastTwo(String(dateObj.getFullYear()));
            case 'H':
                return two(dateObj.getHours());
            case 'G':
                return String(dateObj.getHours());
            case 'i':
                return two(dateObj.getMinutes());
            case 's':
                return two(dateObj.getSeconds());
            default:
                return match;
        }
        // }).replaceAll('"', '');
    })
        .replace(/"/g, '');
    // console.log("formatDate", dateString, ret);
    return ret;
}
/**
 * Display-anchor shim for Chart.js time-axis rendering.
 *
 * The three `datapointMs` converters below shift a "seconds since
 * simulation start" value into a UTC epoch-ms timestamp such that
 * Chart.js's `type: 'time'` scale (which renders via
 * `chartjs-adapter-date-fns` in the **host's local timezone**) displays
 * the X axis as `00:00 → duration` rather than
 * `01:00 → 01:00 + duration` (or whatever the host TZ would prepend).
 *
 * Value: UTC epoch-seconds of local midnight on Jan 1, 1970. Negative
 * for TZs east of UTC (e.g. Europe/Berlin winter: -3600), positive for
 * TZs west of UTC, zero for UTC.
 *
 * ⚠️ Do NOT replace with `-new Date().getTimezoneOffset() * 60`. The
 * anchor MUST be evaluated at Jan 1 1970 (the moment Chart.js will
 * render), not at "now". Using "now" captures the host's CURRENT DST
 * state, which differs from Jan 1 1970's DST state in summer and shifts
 * the chart's X axis by one hour from May through October.
 *
 * Round-trip via these helpers is exact (the offset cancels). The
 * output is a Chart.js internal coordinate, NEVER user-facing time data.
 */
const localMidnightEpochSec = new Date('1970-01-01T00:00:00').getTime() / 1000;
export function datapointMsToSec(ms) {
    return Math.round(ms / 1000 - localMidnightEpochSec);
}
export function datapointMsToMs(ms) {
    return ms - localMidnightEpochSec * 1000;
}
export function secToDatapointMs(sec) {
    return (sec + localMidnightEpochSec) * 1000;
}
